import { useState, useEffect } from 'react';

import headerImage from '../../../assets/icons/header/home/Header section.png';
import TimerCard from '../../../shared/components/TimerCard';
import ViewAllPassesLink from '../../home/components/banner/ViewAllPassesLink';
import type { DiscountBannerData } from '../../home/types/home.types';

interface TimeState {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}



interface DiscountBannerProps {
    discountData?: DiscountBannerData | null;
}

const DiscountBanner = ({ discountData }: DiscountBannerProps) => {
    const [timeState, setTimeState] = useState<{ current: TimeState; previous: TimeState }>({
        current: { days: 0, hours: 0, minutes: 0, seconds: 0 },
        previous: { days: 0, hours: 0, minutes: 0, seconds: 0 },
    });

    useEffect(() => {
        if (!discountData?.countdown) return;

        const target = new Date(discountData.countdown);

        const calculate = (): TimeState => {
            const now = new Date();
            const difference = target.getTime() - now.getTime();

            if (difference > 0) {
                return {
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                };
            }
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        };

        const initialTime = calculate();
        setTimeState({ current: initialTime, previous: initialTime });

        const timer = setInterval(() => {
            const newTime = calculate();
            setTimeState((prev) => ({ current: newTime, previous: prev.current }));
        }, 1000);

        return () => clearInterval(timer);
    }, [discountData?.countdown]);

    if (!discountData) return null;

    return (
        <div
            className="w-full h-auto min-h-[190px] md:min-h-[298px] flex flex-col items-center justify-center py-[24px] md:py-[64px] gap-[8px] md:gap-[64px] bg-cover bg-center bg-no-repeat relative"
            style={{ backgroundImage: `url("${headerImage}")` }}
        >
            <div className="absolute inset-0 bg-black/40 z-10 w-full h-full" />

            <div className="relative z-20 w-full h-auto flex flex-col items-center gap-[12px] md:gap-[24px] text-center px-4 md:px-0">
                <div className="flex flex-col items-center gap-[4px] md:gap-[16px] max-w-[768px]">
                    <h1 className="font-aleo text-[22px] md:text-[36px] font-semibold md:leading-[48px] tracking-[-0.3px] text-white m-0">
                        {discountData.heading}
                    </h1>
                    <p className="font-noto text-[14px] md:text-[16px] font-normal md:font-extralight md:leading-[24px] tracking-normal text-gray-200 m-0">
                        {discountData.description}
                    </p>
                </div>

                {discountData.countdown && (
                    <div className="flex gap-[32px] mt-[8px]">
                        <TimerCard value={timeState.current.days} prevValue={timeState.previous.days} label="Days" />
                        <TimerCard value={timeState.current.hours} prevValue={timeState.previous.hours} label="Hours" />
                        <TimerCard value={timeState.current.minutes} prevValue={timeState.previous.minutes} label="Mins" />
                        <TimerCard value={timeState.current.seconds} prevValue={timeState.previous.seconds} label="Secs" />
                    </div>
                )}

                <ViewAllPassesLink />
            </div>
        </div>
    );
};

export default DiscountBanner;
